import { useState } from "react";
import { Button } from "@/components/ui/button";
import { CreditCard, Loader2, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface RazorpayCheckoutButtonProps {
  purchaseId: string;
  participantId: string;
  participantEmail: string;
  participantName: string;
  amount: number;
  platformFee: number;
  currency: string;
  eventName: string;
  recipientName: string;
  onSuccess: () => void;
  disabled?: boolean;
  hasPaid?: boolean;
}

declare global {
  interface Window {
    Razorpay: any;
  }
}

export function RazorpayCheckoutButton({
  purchaseId,
  participantId,
  participantEmail,
  participantName,
  amount,
  platformFee,
  currency,
  eventName,
  recipientName,
  onSuccess,
  disabled = false,
  hasPaid = false,
}: RazorpayCheckoutButtonProps) {
  const [loading, setLoading] = useState(false);

  const loadRazorpayScript = (): Promise<boolean> => {
    return new Promise((resolve) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    setLoading(true);

    try {
      // Load Razorpay script
      const scriptLoaded = await loadRazorpayScript();
      if (!scriptLoaded) {
        toast.error("Failed to load payment gateway. Please try again.");
        setLoading(false);
        return;
      }

      // Create order via edge function
      const { data: orderData, error: orderError } = await supabase.functions.invoke(
        "create-group-payment-order",
        {
          body: {
            purchaseId,
            participantId,
            amount,
            customerEmail: participantEmail,
            customerName: participantName,
          },
        }
      );

      if (orderError || !orderData) {
        console.error("Order creation error:", orderError);
        toast.error("Failed to create payment order. Please try again.");
        setLoading(false);
        return;
      }

      // Configure Razorpay options
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: "Shakatah",
        description: `Contribution for ${eventName}`,
        order_id: orderData.orderId,
        prefill: {
          name: participantName,
          email: participantEmail,
        },
        notes: {
          purchase_id: purchaseId,
          participant_id: participantId,
        },
        theme: {
          color: "#10b981",
        },
        handler: async function (response: any) {
          // Verify payment
          try {
            const { data: verifyData, error: verifyError } = await supabase.functions.invoke(
              "verify-group-payment",
              {
                body: {
                  razorpay_order_id: response.razorpay_order_id,
                  razorpay_payment_id: response.razorpay_payment_id,
                  razorpay_signature: response.razorpay_signature,
                  participantId,
                  purchaseId,
                },
              }
            );

            if (verifyError || !verifyData?.success) {
              toast.error("Payment verification failed. Please contact support.");
              return;
            }

            toast.success("Payment successful! Thank you for your contribution.");
            onSuccess();
          } catch (err) {
            console.error("Verification error:", err);
            toast.error("Payment verification failed. Please contact support.");
          }
        },
        modal: {
          ondismiss: function () {
            setLoading(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.on("payment.failed", function (response: any) {
        console.error("Payment failed:", response.error);
        toast.error(`Payment failed: ${response.error.description}`);
        setLoading(false);
      });

      razorpay.open();
      setLoading(false);
    } catch (error: any) {
      console.error("Payment error:", error);
      toast.error("An error occurred. Please try again.");
      setLoading(false);
    }
  };

  const totalAmount = amount + platformFee;

  if (hasPaid) {
    return (
      <Button disabled className="w-full gap-2 h-12 text-base bg-emerald-600">
        <CheckCircle2 className="w-5 h-5" />
        Payment Complete
      </Button>
    );
  }

  return (
    <div className="space-y-2">
      <Button
        onClick={handlePayment}
        disabled={disabled || loading}
        className="w-full gap-2 h-12 text-base bg-primary hover:bg-primary/90"
      >
        {loading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Processing...
          </>
        ) : (
          <>
            <CreditCard className="w-5 h-5" />
            Pay ₹{totalAmount.toFixed(0)} via Razorpay
          </>
        )}
      </Button>
      <div className="text-xs text-center text-muted-foreground space-y-1">
        <p>
          ₹{amount.toFixed(0)} to {recipientName} + ₹{platformFee.toFixed(0)} platform fee
        </p>
        <p className="text-[10px]">Supports UPI, Cards, Netbanking & Wallets</p>
      </div>
    </div>
  );
}
